import { useEffect, type ReactNode } from "react";
import { useProduct } from "@/shared/contexts/ProductContext";
import {
  authorizedReturnTarget,
  canAccessCourse,
  courseIdFromPath,
} from "@/shared/lib/courseAccess";

function replaceRoute(path: string) {
  window.history.replaceState({}, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

export function CourseAccessGate({
  pathname,
  children,
}: {
  pathname: string;
  children: ReactNode;
}) {
  const { accessibleCourseIds, loading } = useProduct();
  const courseId = courseIdFromPath(pathname);
  const allowed = !courseId || canAccessCourse(courseId, accessibleCourseIds);

  useEffect(() => {
    if (loading || allowed) return;

    // Do not let an unauthorized protected-route return survive this verdict.
    try {
      const pending = sessionStorage.getItem("loginRedirectTo");
      if (!authorizedReturnTarget(pending, accessibleCourseIds)) {
        sessionStorage.removeItem("loginRedirectTo");
      }
      sessionStorage.removeItem("courseStepData");
    } catch {
      /* storage is optional */
    }
    replaceRoute("/dashboard");
  }, [accessibleCourseIds, allowed, loading]);

  if (loading || !allowed) {
    return (
      <div
        className="flex min-h-[50vh] items-center justify-center bg-[var(--page-bg)]"
        aria-label="Checking course access"
      >
        <div className="h-7 w-7 animate-spin rounded-full border-2 border-[var(--teal-700)] border-t-transparent" />
      </div>
    );
  }

  return children;
}
